import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ControlTaskTableView from './control-task-table-view';
import ReportTaskTableView from './report-task-table-view';
import TaskCard from './task-card-view';
import TaskCommentDrawer from './task-comment-drawer';
import TaskTableView from './task-table-view';
import Spacer, { spc } from '../../base/spacer';
import Text from '../../base/text';

import Button from '~/client/components/base/button';
import SearchAndFiltersBar from '~/client/components/base/search-and-filters-bar';
import { usePermissions } from '~/client/contexts/permissions-context';
import { useDebounce } from '~/client/hooks/policy';
import { useAuthorization } from '~/client/hooks/use-authorization';
import { useUserControlTasksPaginated } from '~/client/hooks/userControlTasksList';
import {
  usePendingReportTaskCount,
  useUserReportTasksPaginated,
} from '~/client/hooks/userReportTasksList';
import { useUserTasksPaginated } from '~/client/hooks/userTasksList';
import { aiDashboardUtils } from '~/client/routes';
import * as GQL from '~/graphql/generated/client';

type FilterTab = GQL.TaskFilter | 'archived';
type ViewMode = 'table' | 'card';

// ── Sub-components ──────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 4,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <Icon
          sx={{ fontSize: 32, color: theme.palette.primary.main, opacity: 0.7 }}
        />
      </Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
        {description}
      </Typography>
    </Box>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const TasksPage: React.FC<{ className?: string }> = () => {
  const theme = useTheme();
  const authorization = useAuthorization();
  const { hasCapability } = usePermissions();
  const navigate = useNavigate();

  const canAccessOrgAdmin = authorization.canAccessOrgAdmin();
  const canAccessDashboard = hasCapability('org:create_project');

  // Within the Tasks panel: assessment-level workflow tasks vs per-question
  // (control) assignments.
  const [taskKind, setTaskKind] = useState<'assessment' | 'control' | 'report'>(
    'assessment'
  );
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [currentTab, setCurrentTab] = useState<GQL.TaskFilter>(
    GQL.TaskFilter.Assigned
  );
  const [isArchivedTab, setIsArchivedTab] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  const [projectType, setProjectType] = useState<GQL.ProjectType | undefined>();
  const [assignmentType, setAssignmentType] = useState<
    GQL.AssignmentType | undefined
  >();
  const [status, setStatus] = useState<GQL.AssignmentStatusType | undefined>();
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<GQL.UserTask | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { tasks, totalCount, hasNextPage, hasPreviousPage, loading, error } =
    useUserTasksPaginated({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      projectType,
      assignmentType,
      status,
      filter: isArchivedTab ? GQL.TaskFilter.All : currentTab,
      isArchived: isArchivedTab,
    });

  // Control (per-question) tasks share the same filter/search/status/page state.
  // The status sub-filter is AssignmentStatusType; map it to ControlAssignmentStatus.
  const controlStatus: GQL.ControlAssignmentStatus | undefined =
    status === GQL.AssignmentStatusType.Completed
      ? GQL.ControlAssignmentStatus.Completed
      : status === GQL.AssignmentStatusType.Pending
        ? GQL.ControlAssignmentStatus.Pending
        : undefined;

  const {
    tasks: controlTasks,
    totalCount: controlTotalCount,
    hasNextPage: controlHasNextPage,
    hasPreviousPage: controlHasPreviousPage,
    loading: controlLoading,
    error: controlError,
  } = useUserControlTasksPaginated({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    projectType,
    status: controlStatus,
    filter: currentTab,
  });

  // Advanced report approvals. Shares the page, search, status and Assigned/All
  // state; the status sub-filter is already AssignmentStatusType so it passes
  // straight through. No project-type dimension — rows are scoped by who the
  // review was routed to and who submitted it, not by project membership.
  const {
    tasks: reportTasks,
    totalCount: reportTotalCount,
    hasNextPage: reportHasNextPage,
    hasPreviousPage: reportHasPreviousPage,
    loading: reportLoading,
    error: reportError,
  } = useUserReportTasksPaginated({
    page,
    pageSize,
    search: debouncedSearch || undefined,
    status,
    // The Assigned/All tabs mean the same here as on the other kinds: mine, or
    // everything I am entitled to see. Passing it through is what stops those
    // tabs looking active while changing nothing.
    filter: currentTab,
  });

  const isControlKind = taskKind === 'control';
  const isReportKind = taskKind === 'report';
  /**
   * Controls that only mean something for assessment tasks.
   *
   * Assignment type, the card/table toggle and the Archived tab are all
   * dimensions the other two kinds do not have. Shown anyway they would read as
   * broken — visibly present, silently ignored — so they are gated on the kind
   * that actually uses them rather than on "not control".
   */
  const isAssessmentKind = taskKind === 'assessment';

  /**
   * Pending approvals, for the badge on the toggle.
   *
   * Its own query rather than a count of the loaded page. The page is shaped by
   * the search box, the status chip and the current page number — all belonging
   * to whichever tab is open — so counting it meant typing in the assessment
   * search silently zeroed the badge for approvals waiting on the report tab,
   * which is precisely the tab the badge exists to draw you to.
   */
  const pendingReportCount = usePendingReportTaskCount();

  const aiSystemHelper = aiDashboardUtils.useHelper();

  /**
   * Open the report's version history, which is where the decision is made.
   *
   * The advanced-reports page is mounted only under the ai-system route shape,
   * and every project type reaches it that way — including organization
   * projects — so this one path serves all of them.
   */
  /**
   * Straight to the report itself.
   *
   * Review and approval live at the end of the report page now, so a task row
   * lands the reader on the document they are being asked to judge rather than
   * on a list they then have to search.
   */
  const handleOpenReport = (task: GQL.ReportTask) => {
    aiSystemHelper.navigateTo('TEMPLATE_REPORT_VIEW', {
      params: { projectId: task.projectId, templateId: task.templateId },
    });
  };

  // Effective values for the shared panel (counts, pagination, body). Keyed by
  // kind rather than nested ternaries now that there are three sources.
  const displayed = {
    assessment: {
      loading,
      totalCount,
      hasNextPage,
      hasPreviousPage,
      rowCount: tasks.length,
      error,
    },
    control: {
      loading: controlLoading,
      totalCount: controlTotalCount,
      hasNextPage: controlHasNextPage,
      hasPreviousPage: controlHasPreviousPage,
      rowCount: controlTasks.length,
      error: controlError,
    },
    report: {
      loading: reportLoading,
      totalCount: reportTotalCount,
      hasNextPage: reportHasNextPage,
      hasPreviousPage: reportHasPreviousPage,
      rowCount: reportTasks.length,
      error: reportError,
    },
  }[taskKind];

  const displayLoading = displayed.loading;
  const displayTotalCount = displayed.totalCount;
  const displayHasNextPage = displayed.hasNextPage;
  const displayHasPreviousPage = displayed.hasPreviousPage;
  const displayRowCount = displayed.rowCount;

  /**
   * May this viewer use the All tab?
   *
   * Org admin, for every task kind alike — assessments, questions and reports.
   * Everyone else works from Assigned to Me, which shows what was routed to
   * them. One rule for the whole page, so a tab means the same thing wherever
   * you are standing on it.
   */
  const canSeeAllTab = canAccessOrgAdmin;

  const activeFilter: FilterTab = isArchivedTab ? 'archived' : currentTab;

  const handleFilterChange = (filter: FilterTab) => {
    if (filter === 'archived') {
      setIsArchivedTab(true);
      setStatus(undefined);
    } else {
      setIsArchivedTab(false);
      setCurrentTab(filter as GQL.TaskFilter);
    }
    setPage(0);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!canAccessOrgAdmin && isArchivedTab) {
      setIsArchivedTab(false);
    }
    if (!canSeeAllTab && currentTab === GQL.TaskFilter.All) {
      setCurrentTab(GQL.TaskFilter.Assigned);
    }
  }, [canAccessOrgAdmin, canSeeAllTab, currentTab, isArchivedTab]);

  /*
    The error for the kind being LOOKED AT. Previously this was the assessment
    query's error unconditionally, which failed in both directions: a broken
    assessment query blanked the page while you were reading report tasks, and a
    broken report query showed nothing at all — an empty "No tasks available"
    panel, indistinguishable from having no approvals waiting. For an approver
    that silence is the failure that matters most.
  */
  if (displayed.error) {
    return (
      <Typography color="error" variant="body2">
        Error loading tasks: {displayed.error.message}
      </Typography>
    );
  }

  // ── Style helpers ──────────────────────────────────────────────────────────

  const isDark = theme.palette.mode === 'dark';

  const filterBtnSx = (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 2.5,
    py: 1.125,
    borderRadius: '999px',
    border: '1px solid',
    borderColor: active ? theme.palette.primary.main : theme.palette.divider,
    bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',

    color: active ? theme.palette.primary.main : theme.palette.text.secondary,

    cursor: 'pointer',
    fontWeight: active ? 600 : 500,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s ease',

    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      bgcolor: alpha(theme.palette.primary.main, 0.06),
    },
  });

  const countChipSx = (active: boolean) => ({
    height: 18,
    minWidth: 18,
    px: 0.5,
    fontSize: '0.7rem',
    fontWeight: 700,
    bgcolor: active
      ? alpha('#fff', 0.25)
      : alpha(theme.palette.text.primary, 0.08),
    color: active ? '#fff' : theme.palette.text.secondary,
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  // ── Panel description ──────────────────────────────────────────────────────

  const panelDesc =
    activeFilter === GQL.TaskFilter.Assigned
      ? 'Tasks assigned to you across all projects'
      : activeFilter === GQL.TaskFilter.All
        ? 'All tasks in the organisation'
        : 'Archived and rejected tasks';

  const emptyTitle =
    activeFilter === GQL.TaskFilter.Assigned
      ? 'No tasks assigned'
      : activeFilter === GQL.TaskFilter.All
        ? 'No tasks available'
        : 'No archived tasks';

  const emptyDesc =
    activeFilter === GQL.TaskFilter.Assigned
      ? "You don't have any tasks assigned to you at the moment. Check back later or contact your project lead."
      : activeFilter === GQL.TaskFilter.All
        ? 'There are no tasks in the organisation at this time.'
        : 'No tasks have been archived or rejected yet.';

  const emptyIcon =
    activeFilter === 'archived' ? ArchiveOutlinedIcon : CheckCircleOutlineIcon;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Text sz="h1">My Work</Text>
          <Text sz="body16" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage your assigned tasks and projects
          </Text>
        </Box>
        <Spacer pb={spc.xxxl} />
      </Box>

      {/* ── Tasks panel ──────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: 'hidden', padding: spc.l }}
      >
        {/* Task kind toggle: Tasks | Assigned Questions | Advanced Report Tasks */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 2.5,
            pb: 0,
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => {
              setTaskKind('assessment');
              setPage(0);
            }}
            sx={filterBtnSx(taskKind === 'assessment')}
          >
            <AssignmentOutlinedIcon sx={{ fontSize: 16 }} />
            <span>Tasks</span>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              setTaskKind('control');
              // Control assignments have no archived concept
              setIsArchivedTab(false);
              setPage(0);
            }}
            sx={filterBtnSx(taskKind === 'control')}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
            <span>Assigned Questions</span>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              setTaskKind('report');
              // Report versions have no archived concept
              setIsArchivedTab(false);
              setPage(0);
            }}
            sx={filterBtnSx(taskKind === 'report')}
          >
            <FolderOutlinedIcon sx={{ fontSize: 16 }} />
            <span>Advanced Report Tasks</span>
            {/*
                A count on the toggle, because otherwise a pending approval is
                invisible unless you happen to open a tab you have never used.
                For an approver this is the whole point of the page.
              */}
            {pendingReportCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  px: 0.75,
                  py: 0.125,
                  borderRadius: '999px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                  bgcolor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                }}
              >
                {pendingReportCount}
              </Box>
            )}
          </Box>
        </Box>

        {/* Panel header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: 2,
            p: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box>
            <Text variant="caption" sz="bold-body14" color="text.secondary">
              {panelDesc}
            </Text>
          </Box>

          {/* Filter buttons + view toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              overflowX: { xs: 'auto', md: 'visible' },
            }}
          >
            {/* Filter pills */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
              {/* Assigned to Me */}
              <Box
                component="button"
                type="button"
                onClick={() => handleFilterChange(GQL.TaskFilter.Assigned)}
                sx={filterBtnSx(activeFilter === GQL.TaskFilter.Assigned)}
              >
                <PersonOutlineIcon sx={{ fontSize: 16 }} />
                <span>Assigned to Me</span>
                {activeFilter === GQL.TaskFilter.Assigned &&
                  displayTotalCount > 0 && (
                    <Box sx={countChipSx(true)}>{displayTotalCount}</Box>
                  )}
              </Box>

              {/* All Tasks — org admin only, for every task kind. */}
              {canSeeAllTab && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleFilterChange(GQL.TaskFilter.All)}
                  sx={filterBtnSx(activeFilter === GQL.TaskFilter.All)}
                >
                  <GroupOutlinedIcon sx={{ fontSize: 15 }} />
                  <span>All Tasks</span>
                  {activeFilter === GQL.TaskFilter.All &&
                    displayTotalCount > 0 && (
                      <Box sx={countChipSx(true)}>{displayTotalCount}</Box>
                    )}
                </Box>
              )}

              {/* Archived — org admin only, assessment tasks only */}
              {canAccessOrgAdmin && isAssessmentKind && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleFilterChange('archived')}
                  sx={filterBtnSx(activeFilter === 'archived')}
                >
                  <ArchiveOutlinedIcon sx={{ fontSize: 15 }} />
                  <span>Archived</span>
                  {activeFilter === 'archived' && displayTotalCount > 0 && (
                    <Box sx={countChipSx(true)}>{displayTotalCount}</Box>
                  )}
                </Box>
              )}
            </Box>

            {/* View mode toggle — assessment tasks only (control tasks are table-only) */}
            {isAssessmentKind && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Tooltip title="Table view">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('table')}
                    sx={{
                      borderRadius: 0,
                      px: 1,
                      bgcolor:
                        viewMode === 'table'
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                      color:
                        viewMode === 'table'
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <TableRowsOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Card view">
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('card')}
                    sx={{
                      borderRadius: 0,
                      px: 1,
                      bgcolor:
                        viewMode === 'card'
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                      color:
                        viewMode === 'card'
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <GridViewOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        {/* Status sub-filters: All / Pending / Completed */}
        {!isArchivedTab && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 2,
                overflowX: 'auto',
              }}
            >
              {(
                [
                  { label: 'All', value: undefined, icon: null },
                  {
                    label: 'Pending',
                    value: GQL.AssignmentStatusType.Pending,
                    icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 14 }} />,
                  },
                  {
                    label: 'Completed',
                    value: GQL.AssignmentStatusType.Completed,
                    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
                  },
                ] as {
                  label: string;
                  value: GQL.AssignmentStatusType | undefined;
                  icon: React.ReactNode;
                }[]
              ).map(({ label, value, icon }) => {
                const active = status === value;
                return (
                  <Box
                    key={label}
                    component="button"
                    type="button"
                    onClick={() => {
                      setStatus(value);
                      setPage(0);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2.5,
                      py: 1.125,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: active
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                      color: active
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      cursor: 'pointer',
                      fontWeight: active ? 600 : 400,
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Search + filters */}
        <Box sx={{ pt: 1.5, pb: 0.5 }}>
          <SearchAndFiltersBar
            placeholder="Search tasks or projects..."
            value={searchInput}
            onChange={setSearchInput}
            onSearchChange={() => setPage(0)}
          >
            {isAssessmentKind && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={assignmentType ?? ''}
                  label="Type"
                  onChange={(e) =>
                    setAssignmentType(
                      e.target.value
                        ? (e.target.value as GQL.AssignmentType)
                        : undefined
                    )
                  }
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value={GQL.AssignmentType.Submission}>
                    Submission
                  </MenuItem>
                  <MenuItem value={GQL.AssignmentType.Review}>Review</MenuItem>
                  <MenuItem value={GQL.AssignmentType.Approval}>
                    Approval
                  </MenuItem>
                  <MenuItem value={GQL.AssignmentType.Audit}>Audit</MenuItem>
                </Select>
              </FormControl>
            )}

            {/*
                Meaningful for assessment and control tasks, which are queried
                per project. Report approvals are scoped by role across the
                whole organization and have no project-type dimension, so the
                filter would sit there appearing to do something.
              */}
            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                display: isReportKind ? 'none' : undefined,
              }}
            >
              <InputLabel>Project Type</InputLabel>
              <Select
                value={projectType ?? ''}
                label="Project Type"
                onChange={(e) =>
                  setProjectType(
                    e.target.value
                      ? (e.target.value as GQL.ProjectType)
                      : undefined
                  )
                }
              >
                <MenuItem value="">All Projects</MenuItem>
                <MenuItem value={GQL.ProjectType.AiSystem}>Project</MenuItem>
                <MenuItem value={GQL.ProjectType.FunctionalModel}>
                  AI Usecase
                </MenuItem>
                <MenuItem value={GQL.ProjectType.ModelCandidate}>
                  Model
                </MenuItem>
                <MenuItem value={GQL.ProjectType.Dataset}>Dataset</MenuItem>
                <MenuItem value={GQL.ProjectType.AgentCandidate}>
                  Agent
                </MenuItem>
                <MenuItem value={GQL.ProjectType.VendorModel}>
                  Vendor Model
                </MenuItem>
                <MenuItem value={GQL.ProjectType.VendorAgent}>
                  Vendor Agent
                </MenuItem>
              </Select>
            </FormControl>
          </SearchAndFiltersBar>
        </Box>

        {/* Table / card / empty / loading */}
        {displayLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 8,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading tasks…
            </Typography>
          </Box>
        ) : displayRowCount === 0 ? (
          <EmptyState
            icon={
              isControlKind
                ? CheckCircleOutlineIcon
                : isReportKind
                  ? FolderOutlinedIcon
                  : emptyIcon
            }
            title={
              isControlKind
                ? 'No questions assigned'
                : isReportKind
                  ? 'No report tasks'
                  : emptyTitle
            }
            description={
              isControlKind
                ? "You don't have any questions assigned to you at the moment."
                : isReportKind
                  ? // Two quite different reasons for an empty list, and the
                    // reader cannot tell them apart, so name both.
                    'Nothing is waiting on you. Advanced Reports appear here when a step is routed to you: as Compliance Reviewer while a report is in review, or as Compliance Approver while it is waiting for approval.'
                  : emptyDesc
            }
          />
        ) : isControlKind ? (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ControlTaskTableView tasks={controlTasks} />
          </Box>
        ) : isReportKind ? (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ReportTaskTableView
              tasks={reportTasks as GQL.ReportTask[]}
              onOpenReport={handleOpenReport}
            />
          </Box>
        ) : viewMode === 'table' ? (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <TaskTableView tasks={tasks} isArchived={isArchivedTab} />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ p: 2.5 }}>
            {tasks.map((task) => (
              <Grid item xs={12} sm={6} lg={4} key={task.taskId}>
                <TaskCard
                  task={task}
                  isArchived={isArchivedTab}
                  onStart={() => {}}
                  onCommentClick={(t) => {
                    setSelectedTask(t);
                    setCommentDrawerOpen(true);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {displayRowCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              onClick={() => displayHasPreviousPage && setPage(page - 1)}
              disabled={!displayHasPreviousPage}
              variant="outlined"
              size="small"
            >
              Previous
            </Button>
            <Typography variant="caption" color="text.secondary">
              Page {page + 1} of{' '}
              {Math.max(1, Math.ceil(displayTotalCount / pageSize))}
              &nbsp;({displayTotalCount} total)
            </Typography>
            <Button
              onClick={() => displayHasNextPage && setPage(page + 1)}
              disabled={!displayHasNextPage}
              variant="outlined"
              size="small"
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Comment drawer ────────────────────────────────────────────── */}
      <TaskCommentDrawer
        open={commentDrawerOpen}
        onClose={() => {
          setCommentDrawerOpen(false);
          timeoutRef.current = setTimeout(() => {
            setSelectedTask(null);
            timeoutRef.current = null;
          }, 300);
        }}
        task={selectedTask}
      />
    </Box>
  );
};

export default TasksPage;
